import { getNotificationRecipients } from "./generated/calls/notification-recipients/notification-recipients";
import { getNotifications } from "./generated/calls/notifications/notifications";
import { postRpcMarkNotificationRead } from "./generated/calls/rpc-mark-notification-read/rpc-mark-notification-read";
import type {
  Notifications as NotificationDTO,
  NotificationRecipients as NotificationRecipientsDTO,
} from "./generated/models";
import { buildHeaders, headersToObject } from "./helpers/headerHelper";
import { postgrestError } from "./helpers/postgrestErrorHelper";
import { postgrestFilter } from "./helpers/queryHelper";
import type { APIResult, APIResultNoContent } from "./types";

export type {
  NotificationDTO,
  NotificationExpandedDTO,
  NotificationRecipientsDTO
};

type NotificationExpandedDTO = NotificationDTO & {
  read_status: "READ" | "UNREAD";
};

/**
 * Fetches all the users notification recipients objects from the API.
 *
 * Returns an API result containing the users fetched notification recipients, or a controlled
 * failure if the request could not be completed.
 */
async function fetchNotificationRecipientsByUserId(
  userId: string,
): Promise<APIResult<NotificationRecipientsDTO[]>> {
  try {
    const headers = buildHeaders();
    const notificationRecipientsFetchRes = await getNotificationRecipients(
      {
        user_id: postgrestFilter(userId),
      },
      { headers },
    );

    switch (notificationRecipientsFetchRes.status) {
      // A read narrowed by `limit`, `offset` or a `Range` header answers 206
      // and carries the same rows as a 200.
      case 200:
      case 206:
        return {
          success: true,
          data: notificationRecipientsFetchRes.data,
        };
      default:
        throw postgrestError("Fetch failed", notificationRecipientsFetchRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Fetches all the requested notifications from the API.
 *
 * Returns an API result containing the fetched notifications, or a controlled
 * failure if the request could not be completed.
 */
async function fetchNotificationsByIds(
  notificationIds: string[],
): Promise<APIResult<NotificationDTO[]>> {
  try {
    const headers = buildHeaders();
    const notificationRecipientsFetchRes = await getNotifications(
      {
        id: postgrestFilter(notificationIds),
      },
      { headers },
    );

    switch (notificationRecipientsFetchRes.status) {
      // A read narrowed by `limit`, `offset` or a `Range` header answers 206
      // and carries the same rows as a 200.
      case 200:
      case 206:
        return {
          success: true,
          data: notificationRecipientsFetchRes.data,
        };
      default:
        throw postgrestError("Fetch failed", notificationRecipientsFetchRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}


export async function fetchNotificationsByUserId(
  userId: string,
): Promise<APIResult<NotificationExpandedDTO[]>> {
  const recipientsResult =
    await fetchNotificationRecipientsByUserId(userId);

  if (!recipientsResult.success) {
    return recipientsResult;
  }

  const notificationIds = recipientsResult.data.map(
    (recipient) => recipient.notification_id,
  );

  const notificationsResult =
    await fetchNotificationsByIds(notificationIds);

  if (!notificationsResult.success) {
    return notificationsResult;
  }

  const readStatusByNotificationId = new Map(
    recipientsResult.data.map((recipient) => [
      recipient.notification_id,
      recipient.read_status,
    ]),
  );

  return {
    success: true,
    data: notificationsResult.data.map((notification) => ({
      ...notification,
      read_status:
        readStatusByNotificationId.get(notification.id) ?? "UNREAD",
    })),
  };
}

/**
 * Marks a notification as read for the current user.
 *
 * Returns an API result indicating success, or a controlled failure if the
 * request could not be completed.
 */
export async function markNotificationRead(
  notificationId: string,
): Promise<APIResultNoContent> {
  try {
    const headers = buildHeaders();
    const response = await postRpcMarkNotificationRead(
      { p_notification_id: notificationId },
      { headers: headersToObject(headers) },
    );

    switch (response.status) {
      case 200:
        return { success: true };
      default:
        throw postgrestError("Marking notification as read failed", response);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}
